import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", surname: "", email: "", password: "", confirm: "" });
  const [consent, setConsent] = useState({ comms_email: true, comms_whatsapp: true, comms_sms: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        first_name: form.first_name, surname: form.surname, email: form.email, password: form.password,
        comms_email: consent.comms_email, comms_whatsapp: consent.comms_whatsapp, comms_sms: consent.comms_sms,
      });
      setUser(data);
      navigate("/profile?welcome=1");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="register-page">
      <div className="hazard-stripes h-2" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link to="/" className="inline-block" data-testid="register-home-link"><Logo /></Link>
            <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl mt-6" data-testid="register-heading">
              Register Your Interest
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              You're building a career in plant operation. Create your applicant account to start.
            </p>
          </div>
          <form onSubmit={submit} className="bg-slate-panel border border-steel rounded-sm p-6 sm:p-8 space-y-5" data-testid="register-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-slate-600">First name</Label>
                <Input id="first_name" required value={form.first_name} onChange={set("first_name")}
                  className="bg-obsidian border-steel text-charcoal h-12" data-testid="register-first-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surname" className="text-slate-600">Surname</Label>
                <Input id="surname" required value={form.surname} onChange={set("surname")}
                  className="bg-obsidian border-steel text-charcoal h-12" data-testid="register-surname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600">Email</Label>
              <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")}
                className="bg-obsidian border-steel text-charcoal h-12" data-testid="register-email" placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600">Password</Label>
              <Input id="password" type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")}
                className="bg-obsidian border-steel text-charcoal h-12" data-testid="register-password" placeholder="Minimum 8 characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-slate-600">Confirm password</Label>
              <Input id="confirm" type="password" required autoComplete="new-password" value={form.confirm} onChange={set("confirm")}
                className="bg-obsidian border-steel text-charcoal h-12" data-testid="register-confirm-password" />
            </div>
            <div className="space-y-2 border-t border-steel pt-4">
              <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Contact preferences</p>
              {[["comms_email", "Email me about my application and appointments"], ["comms_whatsapp", "Contact me by WhatsApp / mobile"], ["comms_sms", "Send me SMS updates"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer" data-testid={`consent-${k}`}>
                  <input type="checkbox" checked={consent[k]} onChange={(e) => setConsent({ ...consent, [k]: e.target.checked })}
                    className="h-4 w-4 accent-[#FFC800]" />
                  {l}
                </label>
              ))}
              <p className="text-[10px] text-slate-400">Operational messages about your application are separate from marketing.</p>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400] h-12 text-base"
              data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Create Applicant Account"}
            </Button>
            <p className="text-center text-sm text-slate-500">
              Already registered?{" "}
              <Link to="/login" className="text-lomond hover:underline font-semibold" data-testid="login-link">Log in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
